import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { WatchlistDrawer } from "@/components/WatchlistDrawer";
import { UserMenu } from "@/components/UserMenu";
import { getToken } from "@/lib/api";

const publicLinks = [
  { to: "/", label: "Home" },
  { to: "/browse", label: "Browse" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [isAdmin, setIsAdmin] = useState(() => !!getToken());
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    setIsAdmin(!!getToken());
  }, [pathname]);

  useEffect(() => {
    const sync = () => setIsAdmin(!!getToken());
    window.addEventListener("storage", sync);
    window.addEventListener("kp:auth", sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener("kp:auth", sync);
    };
  }, []);

  const links = isAdmin ? [...publicLinks, { to: "/admin", label: "Studio" }] : publicLinks;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    navigate(`/browse?q=${encodeURIComponent(q)}`);
    setOpen(false);
  };

  return (
    <motion.header
      data-testid="nav-header"
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-300 ${scrolled || open ? "kp-glass" : "border-b border-transparent"}`}
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 group">
          <Logo size={34} className="transition-transform duration-500 group-hover:rotate-[60deg]" />
          <span className="font-display font-bold tracking-tight text-base sm:text-lg">
            Kuro<span className="text-[#FF2A55]">Pulse</span>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `kp-eyebrow !text-[0.66rem] transition-colors hover:!text-white ${isActive ? "!text-white" : "!text-[#94A3B8]"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <form onSubmit={submit} className="hidden sm:flex items-center gap-2 kp-glass px-3 h-10 w-56 lg:w-72">
            <Search size={15} className="text-[#64748B]" />
            <input
              data-testid="nav-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search anime..."
              className="bg-transparent outline-none text-sm w-full placeholder:text-[#64748B]"
            />
          </form>
          <WatchlistDrawer />
          <UserMenu />
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden p-2 text-white">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="md:hidden px-5 pb-6 flex flex-col gap-4">
          <form onSubmit={submit} className="flex items-center gap-2 border border-white/10 px-3 h-11">
            <Search size={15} className="text-[#64748B]" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search..." className="bg-transparent outline-none text-sm w-full" />
          </form>
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="font-display text-lg">
              {l.label}
            </Link>
          ))}
        </motion.div>
      )}
    </motion.header>
  );
};
